/**
 * PromHighlightBanner — prom-specific Highlight Reel card.
 *
 * Shown on /event/:slug when event.type === 'prom'. Replaces the generic
 * HighlightModule with a free, anon-friendly, share-first experience:
 *  - "🎬 Highlights are ready → Watch now" banner
 *  - Vertical preview
 *  - WhatsApp + Native share with prefilled text
 *  - Tracks shares_count for the growth metric
 *  - "Generate highlights now" button for hosts when not yet ready
 */
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Play, DownloadSimple, ShareNetwork, Sparkle, WhatsappLogo, DeviceMobile } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PromHighlightBanner({ event }) {
  const [status, setStatus] = useState(null);
  const [generating, setGenerating] = useState(false);
  const videoRef = useRef(null);

  const anonId = (() => {
    try {
      let id = localStorage.getItem('anon_id');
      if (!id) { id = 'a_' + Math.random().toString(36).slice(2, 14); localStorage.setItem('anon_id', id); }
      return id;
    } catch { return null; }
  })();

  const fetchStatus = async () => {
    try {
      const r = await axios.get(`${API}/events/${event.event_id}/highlight/public-status`);
      setStatus(r.data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchStatus();
    if (status?.status === 'ready') return; // stop polling once ready
    const t = setInterval(fetchStatus, 3500);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.event_id, status?.status]);

  const hlStatus = status?.status || 'none';

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/events/${event.event_id}/prom-highlight/generate`,
        { anon_id: anonId, trigger: 'manual' });
      toast.success('🎬 Creating your prom highlights...');
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start generation');
    } finally {
      setGenerating(false);
    }
  };

  const trackShare = async (channel) => {
    try {
      await axios.post(`${API}/events/${event.event_id}/highlight/track-share`,
        { anon_id: anonId, channel });
    } catch { /* non-blocking */ }
  };

  const shareUrl = `${window.location.origin}/event/${event.slug}?focus=highlight`;
  const party = String.fromCodePoint(0x1F389); // 🎉 — avoids CRA/webpack source-encoding corruption
  const film = String.fromCodePoint(0x1F3AC);  // 🎬
  const shareText = `Our prom highlights ${party}\n${event.event_name || 'The best night ever'}\nWatch: ${shareUrl}`;

  const handleWhatsApp = () => {
    trackShare('whatsapp');
    const wa = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(wa, '_blank');
  };

  const handleNativeShare = async () => {
    trackShare('native');
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${event.event_name} — Prom Highlights`,
          text: `Our prom highlights ${party}`,
          url: shareUrl,
        });
      } catch { /* user cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(shareText);
        toast.success('Link copied — paste anywhere!');
      } catch {
        toast.error('Could not copy');
      }
    }
  };

  const handleDownload = async () => {
    trackShare('download');
    try {
      const res = await fetch(`${API}/events/${event.event_id}/highlight/download`);
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${(event.event_name || 'prom').replace(/\s+/g, '_')}_highlight.mp4`;
      link.click();
      toast.success('Downloading...');
    } catch {
      toast.error('Download failed');
    }
  };

  const videoUrl = status?.video_url
    ? (status.video_url.startsWith('/api') ? `${BACKEND_URL}${status.video_url}` : status.video_url)
    : '';

  // ── NOT YET READY — show generate CTA or processing ──
  if (hlStatus === 'none' || hlStatus === 'failed') {
    return (
      <div
        className="rounded-2xl mb-8 overflow-hidden border border-[#B89762]/50/25"
        style={{ background: 'linear-gradient(135deg, #0F0723 0%, #1A0530 55%, #0D0D0D 100%)' }}
        data-testid="prom-highlight-banner-idle"
      >
        <div className="p-7">
          <div className="flex items-center gap-2 mb-2">
            <Sparkle size={22} weight="fill" className="text-[#B89762]" />
            <h3 className="font-bold text-xl text-white">Prom Highlight Reel</h3>
            <span className="ml-auto px-2 py-0.5 rounded-full bg-[#B89762]/20 text-[#C29D52] text-xs font-bold">FREE</span>
          </div>
          <p className="text-gray-300 mb-1">A 30-second vertical recap ready to share on WhatsApp, TikTok & Stories.</p>
          <p className="text-gray-500 text-sm mb-6">Auto-creates 4 hours after the prom — or generate now.</p>

          {hlStatus === 'failed' && (
            <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-200 text-sm" data-testid="prom-highlight-retry-hint">
              Last attempt failed — tap below to try again.
            </div>
          )}

          <button
            onClick={handleGenerate}
            disabled={generating}
            className="w-full py-3.5 rounded-xl font-bold text-white text-base disabled:opacity-50 hover:scale-[1.02] transition-all"
            style={{ background: 'linear-gradient(135deg, #B89762, #E879F9)' }}
            data-testid="prom-highlight-generate-btn"
          >
            {generating ? 'Starting...' : '🎬 Generate Highlights Now'}
          </button>
          <p className="text-center text-gray-500 text-xs mt-3">Needs at least 3 uploads. Takes ~2 minutes.</p>
        </div>
      </div>
    );
  }

  // ── PROCESSING ──
  if (hlStatus === 'processing') {
    const progress = status?.progress || 0;
    return (
      <div
        className="rounded-2xl mb-8 overflow-hidden border border-[#B89762]/50/25"
        style={{ background: 'linear-gradient(135deg, #0F0723 0%, #1A0530 55%, #0D0D0D 100%)' }}
        data-testid="prom-highlight-banner-processing"
      >
        <div className="p-7 text-center">
          <Sparkle size={28} weight="fill" className="text-[#B89762] mx-auto mb-3 animate-pulse" />
          <h3 className="font-bold text-lg text-white mb-2">Cooking up your prom highlights...</h3>
          <p className="text-gray-400 text-sm mb-4">Stitching photos, videos & challenge winners.</p>
          <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-1000"
              style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #B89762, #E879F9)' }}
              data-testid="prom-highlight-progress-bar"
            />
          </div>
          <p className="text-gray-500 text-xs mt-3">{progress}% · usually under 2 minutes</p>
        </div>
      </div>
    );
  }

  // ── READY — the money shot ──
  return (
    <div
      className="rounded-2xl mb-8 overflow-hidden border border-[#B89762]/50/30"
      style={{ background: 'linear-gradient(135deg, #1A0530 0%, #0F0723 60%, #0D0D0D 100%)' }}
      data-testid="prom-highlight-banner-ready"
    >
      {/* Banner header — the "🎬 Highlights ready" callout */}
      <div
        className="px-6 py-4 flex items-center gap-3"
        style={{ background: 'linear-gradient(90deg, rgba(232,121,249,0.15), rgba(184,151,98,0.08))' }}
      >
        <span className="text-2xl" role="img" aria-label="film">🎬</span>
        <div className="flex-1">
          <div className="font-bold text-white text-base">Your prom highlights are ready!</div>
          <div className="text-[#C29D52]/85/70 text-xs">
            {status?.clip_count || 0} clips
            {status?.winner_count ? ` · ${status.winner_count} challenge winners` : ''}
            {status?.duration ? ` · ${status.duration}` : ''}
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Vertical preview */}
        <div className="flex justify-center mb-5">
          <div className="relative rounded-2xl overflow-hidden bg-black shadow-2xl" style={{ width: 220, height: 391 }}>
            <video
              ref={videoRef}
              src={videoUrl}
              controls
              playsInline
              poster=""
              className="w-full h-full object-cover"
              data-testid="prom-highlight-video"
            />
          </div>
        </div>

        {/* Share row — WhatsApp first (it's the killer channel for UK prom virality) */}
        <button
          onClick={handleWhatsApp}
          className="w-full py-3.5 rounded-xl font-bold text-white text-base mb-3 hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
          style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}
          data-testid="prom-highlight-share-whatsapp"
        >
          <WhatsappLogo size={20} weight="fill" />
          Share on WhatsApp
        </button>

        <div className="grid grid-cols-3 gap-2">
          <button
            onClick={handleNativeShare}
            className="py-3 rounded-xl border border-white/15 text-white font-semibold text-xs hover:bg-white/5 transition-colors flex items-center justify-center gap-1.5"
            data-testid="prom-highlight-share-native"
          >
            <ShareNetwork size={16} weight="bold" /> Share
          </button>
          <button
            onClick={handleDownload}
            className="py-3 rounded-xl border border-white/15 text-white font-semibold text-xs hover:bg-white/5 transition-colors flex items-center justify-center gap-1.5"
            data-testid="prom-highlight-download"
          >
            <DownloadSimple size={16} weight="bold" /> Save
          </button>
          <button
            onClick={() => videoRef.current?.play?.()}
            className="py-3 rounded-xl border border-white/15 text-white font-semibold text-xs hover:bg-white/5 transition-colors flex items-center justify-center gap-1.5"
            data-testid="prom-highlight-play"
          >
            <Play size={16} weight="fill" /> Play
          </button>
        </div>

        {/* Shares metric — tiny social proof */}
        {status?.shares_count > 0 && (
          <div className="mt-4 text-center text-gray-400 text-xs" data-testid="prom-highlight-shares-count">
            <DeviceMobile size={12} weight="bold" className="inline mr-1" />
            Shared {status.shares_count} {status.shares_count === 1 ? 'time' : 'times'}
          </div>
        )}
      </div>
    </div>
  );
}
